import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  Button,
  Group,
  Modal,
  ScrollAreaAutosize,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconCalendar } from "@tabler/icons-react";

import { StyledEditModalContent } from "@pages/Patients/Laboratory/components/EditModal/EditModal.styled";

import { useCreateSickNote } from "@hooks/mutations/sickNote/useCreateSickNote";
import { useUpdateSickNote } from "@hooks/mutations/sickNote/useUpdateSickNote";

import { SickNote } from "@customTypes/sickNote";


interface ModalProps {
  opened: boolean;
  close: () => void;
  sickNote?: SickNote | null;
}

export const SickNoteModal = ({ opened, close, sickNote }: ModalProps) => {
  const { t } = useTranslation();
  const { patientId, consultationId } = useParams();

  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  const { mutate: createSickNote, isLoading: isCreateSickNoteLoading } =
    useCreateSickNote();
  const { mutate: updateSickNote, isLoading: isUpdateSickNoteLoading } =
    useUpdateSickNote();

  const handleCreateSickNote = () => {
    if (!startDate || !endDate) return;

    const sickNote: Partial<SickNote> = {
      startDate,
      endDate,
      patientId: patientId as string,
      consultationId: consultationId as string,
    };

    createSickNote(sickNote, {
      onSuccess: () => {
        close();
        setStartDate(new Date());
        setEndDate(new Date());
      },
    });
  };

  const handleUpdateSickNote = () => {
    if (!startDate || !endDate) return;

    const updatedSickNote: Partial<SickNote> = {
      id: sickNote?.id,
      startDate,
      endDate,
      patientId: patientId as string,
    };

    updateSickNote(updatedSickNote, {
      onSuccess: () => {
        close();
        setStartDate(new Date());
        setEndDate(new Date());
      },
    });
  };

  useEffect(() => {
    if (sickNote?.startDate) {
      setStartDate(new Date(sickNote.startDate));
      setEndDate(new Date(sickNote.endDate));
    }
  }, [sickNote]);

  return (
    <Modal
      centered
      title={
        <Text fw={500}>
          {sickNote?.startDate ? t("edit-sick-note") : t("create-sick-note")}
        </Text>
      }
      opened={opened}
      onClose={close}
      scrollAreaComponent={ScrollAreaAutosize}>
      <StyledEditModalContent>
        <Stack>
          <DatePickerInput
            leftSection={<IconCalendar size={16} stroke={1.5} />}
            label={t("start-date")}
            minDate={new Date()}
            value={startDate ?? null}
            onChange={setStartDate}
            withAsterisk
          />
          <DatePickerInput
            leftSection={<IconCalendar size={16} stroke={1.5} />}
            label={t("end-date")}
            minDate={startDate ?? new Date()}
            value={endDate ?? null}
            onChange={setEndDate}
            withAsterisk
          />
        </Stack>

        <Group justify="end" mt={"xl"} pb={"md"}>
          <PermissionGate permission={sickNote?.startDate ? "sick_notes.edit" : "sick_notes.create"}><Button
            loading={isCreateSickNoteLoading || isUpdateSickNoteLoading}
            disabled={!startDate || !endDate}
            onClick={
              sickNote?.startDate ? handleUpdateSickNote : handleCreateSickNote
            }>
            {sickNote?.startDate ? t("submit") : t("create-sick-note")}
          </Button></PermissionGate>
        </Group>
      </StyledEditModalContent>
    </Modal>
  );
};
